import { Box, Button, Tooltip } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDeclineSessionMutation } from "../../features/Clients/api";
import { clientsMessages } from "../../features/Clients/messages";
import { I18nContext } from "../../features/I18n/I18nProvider";
import { ConfirmModal } from "../../features/Modal/ConfirmModal";
import { useDevMode } from "../../hooks/useDevMode";
import { Icon } from "../Icon";
import { useMsg } from "../Msg/Msg";

const getIsDateInLessThan = ({ date, hours = 1, now = new Date() }) =>
  date.getTime() - now.getTime() < hours * 60 * 60 * 1000;

const useIsDateInLessThan = ({ hours = 24, date, refreshMs = 1000 }) => {
  const [is, setIs] = useState(getIsDateInLessThan({ date, hours }));

  useEffect(() => {
    const interval = setInterval(() => {
      setIs(getIsDateInLessThan({ date, hours }));
    }, refreshMs);
    return () => clearInterval(interval);
  }, [refreshMs, date, hours]);

  return is;
};

export const useSessionIsNotCancellable = ({ date }) => {
  const isTooLate = useIsDateInLessThan({ hours: 24, date });
  return isTooLate;
};

export const CancelSessionButton = ({
  sessionId,
  time,
  username,
  name,
  type, // "coach" || "user"
  sx = {},
}) => {
  const { i18n } = useContext(I18nContext);
  const msg = useMsg({ dict: clientsMessages });
  const parsed = i18n.parseUTCLocal(time);
  console.log({ parsed, time });
  // TODO: just user?
  // const isNotCancellable = type === "user" && isTooLate;
  const isNotCancellable = useSessionIsNotCancellable({ date: parsed });
  const declineSessionMutation = useDeclineSessionMutation({ type });

  const { show: showConfirmCancel } = ConfirmModal.useModal(
    useMemo(() => {
      return {
        iconName: "RocketLaunch",
        title:
          name || username
            ? msg("clients.upcoming.decline-session.confirm.title", {
                date: i18n.formatLocal(parsed, "Ppppp"),
                name,
                username,
              })
            : msg("clients.upcoming.decline-private-session.confirm.title", {
                date: i18n.formatLocal(parsed, "Ppppp"),
              }),
        desc: "",
        // error: declineSessionMutation.error,
        getButtons: ({ onClose }) => [
          {
            component: Button,
            variant: "contained",
            color: "error",
            type: "button",
            children: msg("clients.upcoming.decline-session.confirm.decline"),
            disabled: declineSessionMutation.isPending,
            loading: declineSessionMutation.isPending,
            onClick: () => declineSessionMutation.mutate({ id: sessionId }),
          },
          {
            variant: "contained",
            type: "button",
            children: msg("clients.upcoming.decline-session.confirm.keep"),
            onClick: () => onClose(),
          },
        ],
      };
    }, [declineSessionMutation, i18n, msg, name, parsed, sessionId, username]),
  );
  const [isDevMode] = useDevMode();

  // https://topleader.atlassian.net/browse/TOP-297

  if (!isDevMode && isNotCancellable)
    return (
      <Tooltip
        placement="top"
        title={msg(
          "clients.upcoming.decline-session.tooltip.less-than-24-hours",
        )}
      >
        {/* Without Box, tooltip is not displayed */}
        <Box sx={sx}>
          <Icon name="Schedule" sx={{ width: 18, height: 18 }} />
        </Box>
      </Tooltip>
    );

  // error msg: "Rescheduling or canceling is not allowed within the last 24 hours before your session to respect the time commitment of your coach. If you cannot attend, please inform your coach directly."
  // instead of button: "Less than 24 hours left. Cancellation or rescheduling is no longer possible."

  return (
    <Tooltip title={declineSessionMutation.error?.message} placement="top">
      <Button
        // size="small"
        sx={{ bgcolor: "white", ...sx }}
        variant="outlined"
        color="error"
        onClick={() => showConfirmCancel()}
        loading={declineSessionMutation.isPending}
      >
        {msg("clients.upcoming.decline-session")}
      </Button>
    </Tooltip>
  );
};
